import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { captureAttribution, getAnalyticsSessionId, trackEvent } from "@/lib/analytics";
import { newRequestKey, requestEntrySource, requestExamples } from "@/lib/requestContext";
import { CONSENT_EVENT, trackSubmittedRequestConversion } from "@/lib/googleTag";
import { categoryByKey, LANGUAGES, readRequestDraft, resolveRequestEntry, TIMINGS } from "@/lib/requestEntry";
import "./AskProblem.css";

export default function AskProblem() {
  const [draft] = useState(readRequestDraft);
  const submissionKey = useRef(draft.clientRequestId || newRequestKey());
  const [description, setDescription] = useState(draft.description || sessionStorage.getItem("en_draft_problem") || "");
  const [media, setMedia] = useState(draft.media || []);
  const [categoryId, setCategoryId] = useState(draft.categoryId || "");
  const [language, setLanguage] = useState(LANGUAGES[draft.language] ? draft.language : "da");
  const [helpTiming, setHelpTiming] = useState(TIMINGS[draft.helpTiming] ? draft.helpTiming : "now");
  const [categories, setCategories] = useState([]);
  const [preferredExpert, setPreferredExpert] = useState(draft.preferredExpert || null);
  const [entry, setEntry] = useState(null);
  const [screen, setScreen] = useState("loading");
  const [showCategoryStep, setShowCategoryStep] = useState(false);
  const [changingCategory, setChangingCategory] = useState(false);
  const [changingLanguage, setChangingLanguage] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [loadAttempt, setLoadAttempt] = useState(0);
  const submitting = useRef(false);
  const fileInput = useRef(null);
  const heading = useRef(null);
  const { user } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  // Entry resolution runs once per mounted flow, so edits cannot be overwritten by attribution.
  const [entryParams] = useState(() => new URLSearchParams(params));
  const entrySource = requestEntrySource(entryParams);
  const resumeDraft = !entrySource || draft.entrySource === entrySource;
  const requestedLanding = resumeDraft && Object.prototype.hasOwnProperty.call(draft, "seoLandingParam")
    ? draft.seoLandingParam : entryParams.get("seo_landing") || entryParams.get("service");
  const [seoLandingParam, setSeoLandingParam] = useState(null);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const [{ data: cats }, landing, expert] = await Promise.all([
          api.get("/categories"),
          requestedLanding ? api.get(`/seo-landings/${encodeURIComponent(requestedLanding)}`).then((r) => r.data).catch(() => null) : null,
          entryParams.get("ekspert") ? api.get(`/experts/${encodeURIComponent(entryParams.get("ekspert"))}`).then((r) => r.data) : null,
        ]);
        if (!active) return;
        if (localStorage.getItem("analytics_consent") === "granted") captureAttribution();
        const resolved = resolveRequestEntry({ params: entryParams, categories: cats, landing, expert });
        const savedCategory = resumeDraft ? categoryByKey(cats, draft.categoryId) : null;
        const initialCategory = savedCategory?.id || resolved.categoryId;
        // Progress describes this visit's screens, independently of acquisition metadata.
        setShowCategoryStep(!initialCategory);
        const initialEntry = resumeDraft && draft.entry ? draft.entry : resolved;
        setCategories(cats);
        setEntry(initialEntry);
        setCategoryId(initialCategory);
        setPreferredExpert(resumeDraft ? draft.preferredExpert || expert : expert);
        setSeoLandingParam(resolved.categoryId === initialCategory ? resolved.seoLandingSlug : null);
        const resumeScreen = draft.screen || (draft.step && draft.step !== 5 ? "form" : "confirm");
        setScreen(!initialCategory ? "category" : resumeDraft && draft.description ? resumeScreen : "form");
        setErr("");
      } catch {
        if (active) { setErr("Vi kunne ikke hente oplysningerne. Prøv igen."); setScreen("loading"); }
      }
    };
    load();
    return () => { active = false; };
  }, [draft, entryParams, entrySource, resumeDraft, requestedLanding, loadAttempt]);

  useEffect(() => {
    if (!entry) return;
    const trackStart = () => trackEvent("request_started", { category: categoryId || null, metadata: entry }, `request-started:${submissionKey.current}:${getAnalyticsSessionId()}`);
    trackStart();
    window.addEventListener(CONSENT_EVENT, trackStart);
    return () => window.removeEventListener(CONSENT_EVENT, trackStart);
  }, [entry, categoryId]);

  useEffect(() => {
    if (screen === "loading" || !entry) return;
    sessionStorage.setItem("en_pending_problem", JSON.stringify({ ...draft, description, categoryId, language, helpTiming, media, preferredExpert, seoLandingParam, entrySource, entry, screen, clientRequestId: submissionKey.current }));
  }, [draft, description, categoryId, language, helpTiming, media, preferredExpert, seoLandingParam, entrySource, entry, screen]);

  useEffect(() => {
    if (screen !== "loading") heading.current?.focus();
  }, [screen]);

  const clearPreferredExpert = () => {
    setPreferredExpert(null);
    const nextParams = new URLSearchParams(params);
    nextParams.delete("ekspert");
    setParams(nextParams, { replace: true });
  };
  const chooseCategory = (id) => {
    if (preferredExpert && id !== categoryId) clearPreferredExpert();
    if (id !== categoryId) {
      setSeoLandingParam(null);
      setEntry((current) => ({ ...current, service_context: null, seoLandingSlug: null }));
    }
    setCategoryId(id);
    setChangingCategory(false);
    setScreen("form");
    setErr("");
  };
  const handleFiles = async (files) => {
    setUploading(true);
    setErr("");
    for (const file of Array.from(files).slice(0, 6 - media.length)) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setMedia((items) => [...items, { name: data.name, type: data.content_type, url: `${process.env.REACT_APP_BACKEND_URL}${data.url}`, path: data.path }]);
      } catch (error) { setErr(formatApiError(error.response?.data?.detail || "Upload fejlede. Prøv igen eller fortsæt uden filen.")); }
    }
    setUploading(false);
    if (fileInput.current) fileInput.current.value = "";
  };
  const validate = () => {
    if (!categories.some((c) => c.id === categoryId)) { setErr("Vælg en kategori."); setScreen("category"); return false; }
    if (description.trim().length < 10) { setErr("Beskriv kort dit problem med mindst 10 tegn."); setScreen("form"); return false; }
    return true;
  };
  const review = (event) => {
    event.preventDefault();
    if (uploading || !validate()) return;
    setErr("");
    setScreen("confirm");
  };
  const signInAndReturn = (returnScreen) => {
    sessionStorage.setItem("en_pending_problem", JSON.stringify({ ...draft, description, categoryId, language, helpTiming, media, preferredExpert, seoLandingParam, entrySource, entry, screen: returnScreen, clientRequestId: submissionKey.current }));
    nav(`/log-ind?next=${encodeURIComponent(`/beskriv-problem?${params.toString()}`)}`);
  };
  const submit = async () => {
    if (submitting.current || uploading || !validate()) return;
    setErr("");
    if (!user) {
      signInAndReturn("confirm");
      return;
    }
    submitting.current = true;
    setBusy(true);
    try {
      const payload = {
        client_request_id: submissionKey.current,
        title: draft.title || description.trim().slice(0, 60),
        description: description.trim(),
        category_id: categoryId,
        help_type: "live",
        duration_min: draft.duration || 15,
        media, language,
        specialties: (draft.specialtiesRaw || "").split(",").map((s) => s.trim()).filter(Boolean),
        what_tried: draft.whatTried || "",
        help_timing: helpTiming,
        preferred_expert_id: preferredExpert?.id || null,
        seo_landing_slug: seoLandingParam || null,
        entry_type: entry.entry_type,
        category_preselected: entry.category_preselected,
      };
      // A timed-out POST may already have created the request. Never retry it automatically.
      const { data } = await api.post("/problems", payload, { timeout: 30000 });
      trackSubmittedRequestConversion(data);
      sessionStorage.removeItem("en_draft_problem");
      sessionStorage.removeItem("en_pending_problem");
      nav(`/dashboard?submitted=${data.id}`);
    } catch (error) {
      setErr(formatApiError(error.response?.data?.detail || "Vi kunne ikke bekræfte afsendelsen. Tjek dine forespørgsler på dit dashboard, før du prøver igen."));
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  };

  const category = categories.find((c) => c.id === categoryId);
  const isIT = ["it-net", "it-teknologi"].includes(category?.slug);
  const total = showCategoryStep ? 3 : 2;
  const step = screen === "confirm" ? total : screen === "form" ? total - 1 : 1;
  const fileSummary = media.length ? `${media.length} ${media.length === 1 ? (media[0].type?.startsWith("image/") ? "billede" : "fil") : "filer"}` : null;
  const categoryPicker = <div className="request-categories" aria-label="Vælg kategori">{categories.map((c) => <button type="button" key={c.id} aria-pressed={categoryId === c.id} onClick={() => chooseCategory(c.id)} data-testid={`cat-pick-${c.slug}`}>{c.name}</button>)}</div>;

  return <section className="request-flow" data-testid="problem-submission-wizard" aria-busy={busy || uploading}>
    {screen !== "loading" && <p className="request-progress">Trin {step} af {total}</p>}
    <h1 ref={heading} tabIndex={-1}>{screen === "confirm" ? "Din forespørgsel" : screen === "category" ? "Hvad har du brug for hjælp til?" : "Hvad skal du have hjælp til?"}</h1>
    {screen === "loading" && <p role="status">{err ? <button type="button" onClick={() => setLoadAttempt((n) => n + 1)}>Prøv igen</button> : "Henter oplysninger…"}</p>}
    {screen === "category" && categoryPicker}
    {screen === "form" && <form onSubmit={review}>
      <label htmlFor="problem-description">Beskriv kort dit problem</label>
      <textarea id="problem-description" data-testid="problem-description" value={description} onChange={(e) => setDescription(e.target.value)} rows={4} minLength={10} required placeholder={isIT ? "Min gaming-PC er faldet fra 120 til 40–50 FPS. Det begyndte for et par dage siden." : requestExamples(category?.slug).description}/>
      <div className="request-upload">
        <p>Tilføj billeder eller video (valgfrit)</p>
        <input ref={fileInput} id="request-files" type="file" accept="image/*,video/*" multiple hidden disabled={uploading || media.length >= 6} data-testid="problem-media-upload" onChange={(e) => handleFiles(e.target.files)}/>
        <button type="button" className="request-secondary" disabled={uploading || media.length >= 6} onClick={() => user ? fileInput.current?.click() : signInAndReturn("form")}>{uploading ? "Uploader…" : "+ Tilføj filer"}</button>
        {!user && <p className="request-meta">Log ind for at tilføje filer. Din beskrivelse gemmes.</p>}
        {media.length > 0 && <ul className="request-files">{media.map((file, i) => <li key={`${file.path}-${i}`}><a href={file.url} target="_blank" rel="noreferrer">{file.name}</a><button type="button" disabled={uploading} aria-label={`Fjern ${file.name}`} data-testid={`remove-media-${i}`} onClick={() => setMedia((items) => items.filter((_, index) => index !== i))}>Fjern</button></li>)}</ul>}
      </div>
      <fieldset><legend>Hvornår har du brug for hjælp?</legend><div className="request-timing">{Object.entries(TIMINGS).map(([value, label]) => <label key={value}><input type="radio" name="timing" value={value} checked={helpTiming === value} onChange={() => setHelpTiming(value)} data-testid={`timing-${value}`}/><span>{label}</span></label>)}</div></fieldset>
      <div className="request-adjustments"><span>Sprog: {LANGUAGES[language]} · <button type="button" aria-expanded={changingLanguage} onClick={() => setChangingLanguage(!changingLanguage)}>Skift</button></span><span>{category?.name} · <button type="button" aria-expanded={changingCategory} onClick={() => setChangingCategory(!changingCategory)}>Skift kategori</button></span></div>
      {changingLanguage && <fieldset className="request-language"><legend>Vælg sprog</legend>{Object.entries(LANGUAGES).map(([value, label]) => <label key={value}><input type="radio" name="language" checked={language === value} onChange={() => { setLanguage(value); setChangingLanguage(false); }} data-testid={`lang-${value}`}/>{label}</label>)}</fieldset>}
      {changingCategory && categoryPicker}
      <button className="request-primary" type="submit" disabled={uploading} data-testid="wizard-next">Find hjælp →</button>
      <p className="request-risk">Gratis at sende · Du bestemmer selv, om du vil acceptere et tilbud.</p>
    </form>}
    {screen === "confirm" && <>
      <div className="request-summary" data-testid="request-summary"><div className="request-summary-heading"><strong>{category?.name}</strong><button type="button" disabled={busy} onClick={() => setScreen("form")}>Rediger</button></div><p className="request-description">{description}</p><p className="request-meta">{[TIMINGS[helpTiming], LANGUAGES[language], fileSummary].filter(Boolean).join(" · ")}</p></div>
      <div className="request-next"><h2>Det sker nu</h2><p>{preferredExpert ? `${preferredExpert.name} får din forespørgsel og kan sende dig et forslag med pris og forventet tidsforbrug.` : "Din forespørgsel sendes til relevante personer, som kan hjælpe. De kan sende dig et forslag med pris og forventet tidsforbrug."}</p><p data-testid="price-disclaimer">Det er gratis at sende en forespørgsel. Du vælger selv, om du vil acceptere et tilbud. Der trækkes ingen betaling, når du sender.</p></div>
      <button type="button" className="request-primary" onClick={submit} disabled={busy} data-testid="wizard-submit">{busy ? "Sender…" : "Send forespørgsel →"}</button>
    </>}
    {screen === "form" && <div className="request-recipient" data-testid="preferred-expert-banner"><strong>{preferredExpert ? `Sendes til ${preferredExpert.name}` : "Sendes til relevante personer"}</strong><p>{preferredExpert ? "Personen kan sende dig et tilbud med pris og forventet tidsforbrug." : "Relevante personer kan se din forespørgsel og sende dig et tilbud."}</p>{preferredExpert && <button type="button" data-testid="clear-preferred-expert" onClick={clearPreferredExpert}>Send til flere personer</button>}</div>}
    {err && <p className="request-error" role="alert" data-testid="wizard-error">{err}</p>}
  </section>;
}
